import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import './PasswordChangeModal.css';

const PasswordChangeModal = ({ user, onClose, onSuccess }) => {
  const [step, setStep] = useState('code');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');

  const has2FA = !!user.dobleFactorActivado;

  const handleSendEmailCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/enviarCodigoRestablecimiento', { email: user.email });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el código');
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (!has2FA) {
      handleSendEmailCode();
    }
  }, [has2FA, handleSendEmailCode]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (has2FA) {
        await api.post('/verificar2FA', { twoFactorCode: code });
      } else {
        const response = await api.post('/verificarCodigoRestablecimiento', {
          email: user.email,
          codigo: code
        });
        setResetToken(response.data.resetToken);
      }
      setStep('password');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Código inválido. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      if (has2FA) {
        await api.put('/cambiarPassword', { newPassword, twoFactorCode: code });
      } else {
        await api.post('/restablecerPassword', { resetToken, newPassword });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pwd-overlay" onClick={onClose}>
      <div className="pwd-modal" onClick={e => e.stopPropagation()}>
        <div className="pwd-step-indicator">
          <div className={`pwd-step-dot ${step === 'password' ? 'done' : 'active'}`}></div>
          <div className={`pwd-step-dot ${step === 'password' ? 'active' : ''}`}></div>
        </div>

        {step === 'code' && (
          <>
            <h3>Verificar identidad</h3>
            <p>
              {has2FA
                ? 'Ingresá el código de 6 dígitos de tu aplicación de autenticación'
                : `Te enviamos un código a ${user.email}. Ingresalo abajo para continuar.`}
            </p>

            {loading && !code && !has2FA && (
              <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
                <i className="bi bi-hourglass-split" style={{ marginRight: 6 }}></i>Enviando código...
              </p>
            )}

            {error && <div className="pwd-error">{error}</div>}

            <form onSubmit={handleVerifyCode}>
              <input
                type="text"
                className="pwd-input-code"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                disabled={loading}
              />
              <button
                type="submit"
                className="pwd-btn"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verificando...' : 'Verificar código'}
              </button>
              {!has2FA && (
                <button
                  type="button"
                  className="pwd-btn-outline"
                  onClick={handleSendEmailCode}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise" style={{ marginRight: 4 }}></i>Reenviar código
                </button>
              )}
              <button type="button" className="pwd-btn-outline" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
            </form>
          </>
        )}

        {step === 'password' && (
          <>
            <h3>Nueva contraseña</h3>
            <p>Ingresá tu nueva contraseña</p>

            {error && <div className="pwd-error">{error}</div>}

            <form onSubmit={handleChangePassword}>
              <div className="pwd-form-group">
                <label>Nueva contraseña</label>
                <input
                  type="password"
                  className="pwd-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={loading}
                  minLength={6}
                />
              </div>
              <div className="pwd-form-group">
                <label>Confirmar nueva contraseña</label>
                <input
                  type="password"
                  className="pwd-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repetí la contraseña"
                  required
                  disabled={loading}
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                className="pwd-btn"
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? 'Guardando...' : 'Guardar contraseña'}
              </button>
              <button type="button" className="pwd-btn-outline" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PasswordChangeModal;
